import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, AlertTriangle, ZoomIn, ZoomOut, Maximize } from "lucide-react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { api, errMsg } from "../lib/api";
import { poBlob } from "./PODocument";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const inp = "h-11 w-full px-3 bg-[#090D16] border border-[#334155] rounded-md focus:outline-none focus:border-[#F59E0B]";
const toB64 = (blob) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob); });

const zb = "h-9 w-9 grid place-items-center rounded-md bg-[#0F1623]/90 border border-[#334155] text-[#E5E7EB] hover:border-[#F59E0B]";

function PagePreview({ pages }) {
  return (
    <div className="relative flex-1 min-h-[520px] h-[60vh] overflow-hidden rounded-sm bg-[#05080F]" data-testid="email-po-preview-pane">
      <TransformWrapper minScale={0.5} maxScale={5} initialScale={1} centerOnInit wheel={{ step: 0.15 }} doubleClick={{ mode: "zoomIn" }}>
        {({ zoomIn, zoomOut, resetTransform }) => (<>
          <div className="absolute top-2 right-2 z-10 flex gap-1">
            <button data-testid="email-po-zoom-in" onClick={() => zoomIn()} className={zb} title="Zoom in"><ZoomIn className="w-4 h-4" /></button>
            <button data-testid="email-po-zoom-out" onClick={() => zoomOut()} className={zb} title="Zoom out"><ZoomOut className="w-4 h-4" /></button>
            <button data-testid="email-po-zoom-reset" onClick={() => resetTransform()} className={zb} title="Fit"><Maximize className="w-4 h-4" /></button>
          </div>
          <TransformComponent wrapperStyle={{ width: "100%", height: "100%" }} contentStyle={{ width: "100%" }}>
            <div className="w-full flex flex-col items-center gap-4 p-4">
              {pages.map((p, i) => <img key={i} src={p.src} alt={`PO page ${i + 1}`} data-testid={`email-po-page-${i + 1}`} draggable={false} className="w-full max-w-[640px] h-auto bg-white shadow-[0_8px_30px_rgba(0,0,0,0.6)] select-none" />)}
            </div>
          </TransformComponent>
        </>)}
      </TransformWrapper>
    </div>
  );
}

// Review email copy + PO PDF attachment, then send over SMTP
export default function EmailPODialog({ poId, open, onClose, onSent }) {
  const [d, setD] = useState(null);
  const [po, setPo] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !poId) return undefined;
    let url;
    setD(null); setPdf(null);
    (async () => {
      const [dr, p, tpl] = await Promise.all([api.get(`/purchase-orders/${poId}/email-draft`), api.get(`/purchase-orders/${poId}`), api.get("/po-template")]);
      setD(dr.data); setPo(p.data);
      const blob = await poBlob(p.data, tpl.data);
      url = URL.createObjectURL(blob);
      const pv = await api.post("/po-email/preview", { pdf_base64: await toB64(blob) });
      setPdf({ blob, url, pages: pv.data.pages });
    })().catch((e) => toast.error(errMsg(e)));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [open, poId]);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/purchase-orders/${poId}/email`, { subject: d.subject, body: d.body, pdf_base64: await toB64(pdf.blob) });
      toast.success(`${po.po_number} emailed to ${d.to} · marked Sent`); window.dispatchEvent(new Event("ff-po-email-changed")); onSent?.(r.data); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const blocked = !d || d.missing_email || !d.configured;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F1623] border-[#334155] max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="email-po-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Review PO Email{po ? ` · ${po.po_number}` : ""}</DialogTitle></DialogHeader>
        {!d ? <div className="font-mono text-[#6B7280] p-6">PREPARING DRAFT…</div> : (
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="space-y-3">
              {!d.configured && <div data-testid="email-po-not-configured" className="p-3 rounded-md border border-[#F59E0B] text-sm text-[#F59E0B] flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0" />Set up SMTP in Integrations → Email before sending.</div>}
              {d.missing_email && <div data-testid="email-po-missing-email" className="p-3 rounded-md border border-[#EF4444] text-sm text-[#EF4444] flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0" />This supplier has no valid email address. Add one on the Suppliers page.</div>}
              <div className="grid grid-cols-[60px_1fr] gap-2 items-center text-sm">
                <span className="font-mono text-[10px] text-[#6B7280]">FROM</span><span data-testid="email-po-from" className="font-mono text-xs">{d.from || "—"}</span>
                <span className="font-mono text-[10px] text-[#6B7280]">TO</span><span data-testid="email-po-to" className="font-mono text-xs">{d.to || "—"}</span>
                <span className="font-mono text-[10px] text-[#6B7280]">CC</span><span data-testid="email-po-cc" className="font-mono text-xs">{d.cc || "—"}</span>
              </div>
              <input data-testid="email-po-subject" value={d.subject} onChange={(e) => setD({ ...d, subject: e.target.value })} className={inp} />
              <textarea data-testid="email-po-body" value={d.body} onChange={(e) => setD({ ...d, body: e.target.value })} rows={14} className={`${inp} h-auto py-2 font-mono text-xs`} />
              <div className="font-mono text-[10px] text-[#6B7280]">ATTACHMENT · {po?.po_number}.pdf</div>
              <button data-testid="email-po-send-btn" disabled={blocked || !pdf || busy} onClick={send} className="h-14 w-full rounded-md bg-[#F59E0B] text-[#090D16] font-display font-extrabold flex items-center justify-center gap-2 disabled:opacity-40"><Send className="w-4 h-4" />{busy ? "SENDING…" : "APPROVE & SEND"}</button>
            </div>
            <div className="flex flex-col rounded-md bg-[#090D16] p-3 min-h-[560px]" data-testid="email-po-pdf-preview">
              <div className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280] mb-2">DOCUMENT PREVIEW · PINCH, SCROLL OR DRAG TO ZOOM AND PAN</div>
              {pdf ? <PagePreview pages={pdf.pages} /> : <div className="flex-1 grid place-items-center text-[#6B7280] font-mono text-xs">RENDERING PREVIEW…</div>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
